import { useState, useMemo, useRef, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Loader2, Camera, Search, Maximize2, ExternalLink, Wifi, WifiOff, Copy, Check, Film, ChevronDown, AlertCircle, Play, X, Upload, Trash2, Download, ListVideo, FileText, Calendar as CalendarIcon } from 'lucide-react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { format } from 'date-fns';
import { DashboardLayout } from '@/components/dashboard/DashboardLayout';
import { useTraccarSession } from '@/hooks/useTraccarSession';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { Calendar as CalendarComponent } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { fetchDevices, type Device } from '@/lib/traccar-api';
import { searchHikvisionRecordings, type HikvisionSearchResult, type HikvisionSegment } from '@/lib/hikvision-api';
import { startJimiStreaming, getJimiWhepUrl, requestJimiSdCardFileList, requestJimiHistoryVideoUpload } from '@/lib/jimi-api';
import { getJt1078Channels, getJt1078WhepUrl, isJt1078Camera, startJt1078Streaming, waitForJt1078Video } from '@/lib/jt1078-api';
import { fetchRecordings, deleteRecording, type DeviceRecording } from '@/lib/reseller-api';
import { useToast } from '@/hooks/use-toast';

// --- Hikvision recordings helpers ---

const toLocalDatetimeValue = (d: Date) => {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const toISOZ = (localValue: string) => new Date(localValue).toISOString().replace(/\.\d{3}Z$/, 'Z');

const formatTime = (iso: string) =>
  new Date(iso).toLocaleString(undefined, { dateStyle: 'short', timeStyle: 'medium' });

const formatDuration = (startIso: string, endIso: string) => {
  const s = Math.round((new Date(endIso).getTime() - new Date(startIso).getTime()) / 1000);
  const m = Math.floor(s / 60);
  const sec = s % 60;
  return m > 0 ? `${m}m ${sec}s` : `${sec}s`;
};

const formatSize = (bytes: number) => {
  if (bytes >= 1e9) return `${(bytes / 1e9).toFixed(1)} GB`;
  return `${(bytes / 1e6).toFixed(0)} MB`;
};

interface HikConfig { workerUrl: string; hikHost: string; hikDevIndex: string; hikTrackID: number; user: string; pass: string; videoBaseUrl: string; }

const buildPlaybackUrl = (rtspUri: string, videoBaseUrl: string, user: string, pass: string): string => {
  if (videoBaseUrl) {
    // Extract devIndex, starttime, endtime from the Hikvision RTSP playback URI:
    // rtsp://host:port/dac/playback/camera/{devIndex}/MAIN/TCP?starttime=...&endtime=...
    const m = rtspUri.match(/\/dac\/playback\/camera\/([^/]+)\/.*[?&]starttime=([^&]+)&endtime=([^&]+)/);
    if (m) {
      const [, devIndex, start, end] = m;
      return `${videoBaseUrl.replace(/\/$/, '')}/playback/${devIndex}/${start}/${end}`;
    }
  }
  // Fallback: embed credentials in RTSP URL
  if (user && (rtspUri.startsWith('rtsp://') || rtspUri.startsWith('rtsps://'))) {
    const scheme = rtspUri.startsWith('rtsps://') ? 'rtsps' : 'rtsp';
    const rest = rtspUri.slice(scheme.length + 3);
    return `${scheme}://${encodeURIComponent(user)}:${encodeURIComponent(pass)}@${rest}`;
  }
  return rtspUri;
};

const getHikvisionConfig = (device: Device): HikConfig | null => {
  try {
    const saved = localStorage.getItem('app_settings');
    const s = saved ? JSON.parse(saved) : {};
    const hikHost = (s.hikBaseUrl ?? 'https://hikvision.entrack-plataforma.workers.dev').trim();
    const workerUrl = (s.hikWorkerUrl ?? '').trim() || hikHost;
    const user = (s.hikUser ?? 'admin').trim();
    const pass = (s.hikPassword ?? '1937armada').trim();
    const hikTrackID = typeof s.hikTrackID === 'number' ? s.hikTrackID : 101;
    const attrs = device.attributes || {};
    const hikDevIndex = typeof attrs.hikDevIndex === 'string' ? attrs.hikDevIndex.trim() : '';
    const videoBaseUrl = (s.hikVideoBaseUrl ?? 'https://video.jsrastreamento.com').trim();
    if (!hikHost) console.warn('getHikvisionConfig: missing hikBaseUrl');
    if (!hikDevIndex) console.warn('getHikvisionConfig: missing hikDevIndex on device', device.id);
    return hikHost && hikDevIndex ? { workerUrl, hikHost, hikDevIndex, hikTrackID, user, pass, videoBaseUrl } : null;
  } catch (e) {
    console.error('getHikvisionConfig', e)
    return null;
  }
};

interface RecordingItemProps {
  item: HikvisionSegment;
  user: string;
  pass: string;
  videoBaseUrl: string;
}

function RecordingItem({ item, user, pass, videoBaseUrl }: RecordingItemProps) {
  const { t } = useTranslation();
  const [playing, setPlaying] = useState(false);
  const rawUri = item.mediaSegmentDescriptor.playbackURI;
  const authUri = buildPlaybackUrl(rawUri, videoBaseUrl, user, pass);
  const isHls = authUri.startsWith('https://');
  const hlsSrc = isHls ? `${authUri.replace(/\/$/, '')}/index.m3u8` : null;

  return (
    <div className="py-2.5 border-b last:border-0">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 text-sm">
          <p className="font-medium">{formatTime(item.timeSpan.startTime)} → {formatTime(item.timeSpan.endTime)}</p>
          <p className="text-xs text-muted-foreground mt-0.5">
            {formatDuration(item.timeSpan.startTime, item.timeSpan.endTime)}
            {' · '}
            {formatSize(item.mediaSegmentDescriptor.size)}
            {' · '}
            {item.mediaSegmentDescriptor.codecType}
          </p>
        </div>
        <Button variant="ghost" size="sm" className="shrink-0 h-7 px-2" onClick={() => setPlaying(p => !p)}>
          {playing ? <X className="h-3.5 w-3.5" /> : <Play className="h-3.5 w-3.5" />}
          <span className="ml-1 text-xs">{playing ? t('cameras.close') : t('cameras.play')}</span>
        </Button>
      </div>
      {playing && hlsSrc && (
        <video
          src={hlsSrc}
          autoPlay
          controls
          playsInline
          className="w-full mt-2 rounded"
        />
      )}
    </div>
  );
}

interface RecordingsDialogProps {
  device: Device;
  onClose: () => void;
}

function RecordingsDialog({ device, onClose }: RecordingsDialogProps) {
  const { t } = useTranslation();
  const cfg = getHikvisionConfig(device);

  const now = new Date();
  const yesterday = new Date(now.getTime() - 24 * 60 * 60 * 1000);
  const [startVal, setStartVal] = useState(toLocalDatetimeValue(yesterday));
  const [endVal, setEndVal] = useState(toLocalDatetimeValue(now));
  const [trackID, setTrackID] = useState(cfg?.hikTrackID ?? 101);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<HikvisionSearchResult | null>(null);
  const [allSegments, setAllSegments] = useState<HikvisionSegment[]>([]);
  const searchIdRef = useRef<string>('');

  const doSearch = useCallback(async (position = 0, id?: string) => {
    if (!cfg) return;
    setLoading(true);
    setError(null);
    try {
      const res = await searchHikvisionRecordings(
        cfg.workerUrl, 'http://video-no-proxy.jsrastreamento.com:8080', cfg.hikDevIndex,
        cfg.user, cfg.pass,
        trackID,
        toISOZ(startVal),
        toISOZ(endVal),
        position,
        40,
        id,
      );
      searchIdRef.current = res.searchID;
      const segments = res.matchList.map(m => m.searchMatchItem);
      setAllSegments(prev => position === 0 ? segments : [...prev, ...segments]);
      setResult(res);
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
    }
  }, [cfg, startVal, endVal, trackID]);

  const handleSearch = () => {
    searchIdRef.current = '';
    setAllSegments([]);
    setResult(null);
    doSearch(0);
  };

  const handleLoadMore = () => {
    doSearch(allSegments.length, searchIdRef.current);
  };

  return (
    <Dialog open onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent className="max-w-xl w-full gap-0" aria-describedby={undefined}>
        <DialogTitle className="flex items-center gap-2 pb-4 border-b">
          <Film className="h-4 w-4 text-muted-foreground" />
          {t('cameras.recordingsFor', { name: device.name })}
        </DialogTitle>

        <div className="flex flex-col gap-3 pt-4 pb-2">
          {!cfg && (
            <div className="flex items-start gap-2 rounded-md bg-amber-500/10 text-amber-600 dark:text-amber-400 text-sm p-3">
              <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
              <span>{t('cameras.hikNotConfigured')}</span>
            </div>
          )}
          <div className="flex flex-wrap gap-2 items-end">
            <div className="flex-1 min-w-[160px]">
              <label className="text-xs text-muted-foreground mb-1 block">{t('cameras.fromTime')}</label>
              <Input type="datetime-local" value={startVal} onChange={e => setStartVal(e.target.value)} className="text-sm" />
            </div>
            <div className="flex-1 min-w-[160px]">
              <label className="text-xs text-muted-foreground mb-1 block">{t('cameras.toTime')}</label>
              <Input type="datetime-local" value={endVal} onChange={e => setEndVal(e.target.value)} className="text-sm" />
            </div>
            <div className="w-20">
              <label className="text-xs text-muted-foreground mb-1 block">{t('cameras.trackID')}</label>
              <Input type="number" value={trackID} onChange={e => setTrackID(parseInt(e.target.value) || 101)} className="text-sm" />
            </div>
            <Button onClick={handleSearch} disabled={loading || !cfg} className="shrink-0">
              {loading && !allSegments.length
                ? <><Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />{t('cameras.searching')}</>
                : t('cameras.searchRecordings')}
            </Button>
          </div>

          {error && (
            <div className="flex items-start gap-2 rounded-md bg-destructive/10 text-destructive text-sm p-3">
              <AlertCircle className="h-4 w-4 mt-0.5 shrink-0" />
              <span>{error}</span>
            </div>
          )}

          {result !== null && (
            <div className="mt-1 max-h-[380px] overflow-y-auto pr-1">
              {allSegments.length === 0
                ? <p className="text-sm text-muted-foreground text-center py-8">{t('cameras.noRecordings')}</p>
                : (
                  <>
                    <p className="text-xs text-muted-foreground mb-2">
                      {allSegments.length} {t('cameras.clipsShown')}
                      {result.responseStatusStrg === 'MORE' ? ` (${t('cameras.moreAvailable')})` : ''}
                    </p>
                    {allSegments.map(seg => (
                      <RecordingItem key={seg.sourceID} item={seg} user={cfg?.user ?? ''} pass={cfg?.pass ?? ''} videoBaseUrl={cfg?.videoBaseUrl ?? ''} />
                    ))}
                    {result.responseStatusStrg === 'MORE' && (
                      <Button
                        variant="outline"
                        size="sm"
                        className="w-full mt-3"
                        disabled={loading}
                        onClick={handleLoadMore}
                      >
                        {loading
                          ? <><Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />{t('cameras.searching')}</>
                          : <><ChevronDown className="h-3.5 w-3.5 mr-1.5" />{t('cameras.loadMore')}</>}
                      </Button>
                    )}
                  </>
                )}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

// --- Jimi Recordings ---

const formatFileSize = (bytes: number | null) => {
  if (!bytes) return '—';
  if (bytes >= 1e9) return `${(bytes / 1e9).toFixed(1)} GB`;
  if (bytes >= 1e6) return `${(bytes / 1e6).toFixed(1)} MB`;
  return `${(bytes / 1e3).toFixed(0)} KB`;
};

const getJimiCameraLabel = (channel: number | null) => {
  if (channel === 1) return 'Front camera';
  if (channel === 2) return 'Cabin camera';
  if (channel === 0) return 'Front camera';
  return channel != null ? `Camera ${channel}` : 'Camera';
};

const extractJimiFilenameMetadata = (...values: Array<string | null | undefined>) => {
  const source = values.filter(Boolean).join(' ');
  const marked = source.match(/(\d{4})_(\d{2})_(\d{2})_(\d{2})_(\d{2})_(\d{2})_([A-Z])_(\d+)\.(mp4|avi|mkv)/i);
  const spaced = source.match(/(\d{4})_(\d{2})_(\d{2})_(\d{2})_(\d{2})_(\d{2})_(\d+)\.(mp4|avi|mkv)/i);
  const compact = source.match(/(\d{4})(\d{2})(\d{2})(\d{2})(\d{2})(\d{2})[_-](\d+)\.(mp4|avi|mkv)/i);
  const match = marked ?? spaced ?? compact;
  if (!match) return null;
  const [, y, mo, d, h, mi, s] = match;
  const ch = marked ? match[8] : match[7];
  const channel = Number(ch);
  const commandTimestamp = `${y}_${mo}_${d}_${h}_${mi}_${s}`;
  return {
    timeLabel: `${y}-${mo}-${d} ${h}:${mi}:${s}`,
    dateKey: `${y}-${mo}-${d}`,
    channel,
    command: `HVIDEO,${commandTimestamp},${channel}`,
  };
};

const getJimiRecordingDateKey = (rec: DeviceRecording) => {
  const meta = extractJimiFilenameMetadata(rec.filename, rec.storage_path, rec.storage_url);
  if (meta?.dateKey) return meta.dateKey;
  return format(new Date(rec.recorded_at ?? rec.created_at), 'yyyy-MM-dd');
};

interface JimiRecordingRowProps {
  rec: DeviceRecording;
  onDelete: (id: string) => void;
  deleting: boolean;
  onOpenFileList: (rec: DeviceRecording) => void;
}

function JimiRecordingRow({ rec, onDelete, deleting, onOpenFileList }: JimiRecordingRowProps) {
  const { t } = useTranslation();
  const [playing, setPlaying] = useState(false);
  const playUrl = rec.signed_url ?? rec.storage_url;
  const isFileList = rec.filename.endsWith('.txt') || rec.filename.includes('sdcard-list') || rec.storage_path.includes('sdcard-list');
  const filenameMeta = extractJimiFilenameMetadata(rec.filename, rec.storage_path, rec.storage_url);
  const displayTime = filenameMeta?.timeLabel
    ?? format(new Date(rec.recorded_at ?? rec.created_at), 'MMM d, HH:mm:ss');
  const displayChannel = filenameMeta?.channel ?? rec.channel;
  const sourceLabel = isFileList ? t('cameras.sdCardFileList') : t('cameras.sdCardVideo');

  return (
    <div className="py-2.5 border-b last:border-0">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 text-sm">
          <div className="flex items-center gap-2">
            <p className="font-medium">
              {displayTime}
            </p>
            {!isFileList && (
              <Badge variant="secondary" className="h-5 px-1.5 text-[11px]">
                {getJimiCameraLabel(displayChannel)}
              </Badge>
            )}
          </div>
          <p className="text-xs text-muted-foreground mt-0.5">
            {sourceLabel}
            {!isFileList ? ` · ${formatFileSize(rec.file_size)}` : ''}
            {filenameMeta?.command ? ` · ${filenameMeta.command}` : ''}
          </p>
          <p className="text-[11px] text-muted-foreground/80 font-mono truncate mt-0.5">
            {rec.filename}
          </p>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          {isFileList && (
            <Button
              variant="ghost"
              size="sm"
              className="h-7 px-2"
              onClick={() => onOpenFileList(rec)}
              title={t('cameras.viewFileList')}
            >
              <FileText className="h-3.5 w-3.5" />
            </Button>
          )}
          {playUrl && !isFileList && (
            <Button variant="ghost" size="sm" className="h-7 px-2" onClick={() => setPlaying(p => !p)}>
              {playing ? <X className="h-3.5 w-3.5" /> : <Play className="h-3.5 w-3.5" />}
            </Button>
          )}
          {playUrl && (
            <Button variant="ghost" size="sm" className="h-7 px-2" asChild>
              <a href={playUrl} download={rec.filename} title={t('cameras.download')}>
                <Download className="h-3.5 w-3.5" />
              </a>
            </Button>
          )}
          <Button
            variant="ghost"
            size="sm"
            className="h-7 px-2 text-destructive hover:text-destructive"
            disabled={deleting}
            onClick={() => onDelete(rec.id)}
          >
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>
      {playing && playUrl && !isFileList && (
        <video src={playUrl} autoPlay controls playsInline className="w-full mt-2 rounded" />
      )}
    </div>
  );
}

interface SdCardListEntry {
  filename: string;
  timeLabel: string | null;
  channel: number | null;
  cameraLabel: string;
  sourceLabel: string;
  command: string;
}

const parseSdCardList = (text: string): { imei: string; entries: SdCardListEntry[] } => {
  let imei = text.match(/\b\d{15}\b/)?.[0] ?? '';
  let namesText = text;
  try {
    const parsed = JSON.parse(text) as { imei?: string; fileNameList?: string };
    imei = parsed.imei ?? imei;
    namesText = parsed.fileNameList ?? text;
  } catch {
    // Some firmware sends plain comma/newline separated filenames.
  }

  const filenames = Array.from(new Set(
    namesText
      .split(/[\s,]+/)
      .map(name => name.trim().replace(/^"|"$/g, ''))
      .filter(name => /^\d{4}_\d{2}_\d{2}_\d{2}_\d{2}_\d{2}_\d+\.mp4$/i.test(name))
  ));

  const entries = filenames.map(filename => {
    const match = filename.match(/^(\d{4})_(\d{2})_(\d{2})_(\d{2})_(\d{2})_(\d{2})_(\d+)\.mp4$/i);
    if (!match) return {
      filename,
      timeLabel: null,
      channel: null,
      cameraLabel: 'Unknown camera',
      sourceLabel: 'SD card video',
      command: '',
    };
    const [, y, mo, d, h, mi, s, ch] = match;
    const channel = Number(ch);
    const timestamp = `${y}_${mo}_${d}_${h}_${mi}_${s}`;
    return {
      filename,
      timeLabel: `${y}-${mo}-${d} ${h}:${mi}:${s}`,
      channel,
      cameraLabel: getJimiCameraLabel(channel),
      sourceLabel: 'SD card video',
      command: `HVIDEO,${timestamp},${channel}`,
    };
  });

  return { imei, entries };
};

interface SdCardFileListDialogProps {
  device: Device;
  rec: DeviceRecording;
  recordings: DeviceRecording[];
  onClose: () => void;
}

function SdCardFileListDialog({ device, rec, recordings, onClose }: SdCardFileListDialogProps) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const [text, setText] = useState('');
  const [loading, setLoading] = useState(true);
  const [requesting, setRequesting] = useState<string | null>(null);
  const [bulkProgress, setBulkProgress] = useState<{ current: number; total: number } | null>(null);
  const playUrl = rec.signed_url ?? rec.storage_url;

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    if (!playUrl) {
      setLoading(false);
      return;
    }
    fetch(playUrl)
      .then(res => res.text())
      .then(body => { if (!cancelled) setText(body); })
      .catch(() => { if (!cancelled) toast({ title: t('cameras.fileListLoadError'), variant: 'destructive' }); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [playUrl, t, toast]);

  const parsed = useMemo(() => parseSdCardList(text), [text]);
  const uploadedFileKeys = useMemo(() => new Set(
    recordings.flatMap(item => {
      const meta = extractJimiFilenameMetadata(item.filename, item.storage_path, item.storage_url);
      return [item.filename, item.storage_path, meta?.command].filter(Boolean);
    })
  ), [recordings]);
  const isEntryUploaded = useCallback(
    (entry: SdCardListEntry) => uploadedFileKeys.has(entry.filename) || uploadedFileKeys.has(entry.command),
    [uploadedFileKeys],
  );
  const missingEntries = useMemo(
    () => parsed.entries.filter(entry => !isEntryUploaded(entry)),
    [parsed.entries, isEntryUploaded],
  );

  const handleUpload = async (filename: string) => {
    setRequesting(filename);
    try {
      await requestJimiHistoryVideoUpload(device.id, filename);
      toast({ title: t('cameras.historyUploadRequested') });
    } catch {
      toast({ title: t('cameras.historyUploadRequestError'), variant: 'destructive' });
    } finally {
      setRequesting(null);
    }
  };

  const handleUploadAll = async () => {
    const entries = missingEntries.map(entry => entry.filename);
    setBulkProgress({ current: 0, total: entries.length });
    try {
      for (let i = 0; i < entries.length; i += 1) {
        setBulkProgress({ current: i + 1, total: entries.length });
        setRequesting(entries[i]);
        await requestJimiHistoryVideoUpload(device.id, entries[i]);
        await new Promise(resolve => setTimeout(resolve, 350));
      }
      toast({ title: t('cameras.bulkHistoryUploadRequested') });
    } catch {
      toast({ title: t('cameras.bulkHistoryUploadRequestError'), variant: 'destructive' });
    } finally {
      setRequesting(null);
      setBulkProgress(null);
    }
  };

  return (
    <Dialog open onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent className="max-w-3xl w-full gap-0" aria-describedby={undefined}>
        <DialogTitle className="flex items-center gap-2 pb-4 border-b">
          <ListVideo className="h-4 w-4 text-muted-foreground" />
          {t('cameras.sdCardFileList')}
        </DialogTitle>
        <div className="py-3 border-b flex items-center justify-between gap-3">
          <span className="text-xs text-muted-foreground">
            {parsed.imei || rec.device_imei} · {parsed.entries.length} {t('cameras.filesShown')}
            {` · ${missingEntries.length} ${t('cameras.filesMissing')}`}
            {bulkProgress ? ` · ${bulkProgress.current}/${bulkProgress.total}` : ''}
          </span>
          <Button
            variant="outline"
            size="sm"
            className="h-8 px-2 shrink-0"
            disabled={loading || missingEntries.length === 0 || Boolean(bulkProgress)}
            onClick={handleUploadAll}
          >
            {bulkProgress
              ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" />
              : <Upload className="h-3.5 w-3.5 mr-1.5" />}
            {t('cameras.uploadMissingFiles')}
          </Button>
        </div>
        <div className="max-h-[520px] overflow-y-auto">
          {loading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : parsed.entries.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-10">{t('cameras.noSdCardFiles')}</p>
          ) : (
            parsed.entries.map(entry => (
              <div key={entry.filename} className="flex items-center justify-between gap-3 py-2.5 border-b last:border-0">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-medium">
                      {entry.timeLabel ?? t('cameras.unknownTime')}
                    </p>
                    <Badge variant="secondary" className="h-5 px-1.5 text-[11px]">
                      {entry.cameraLabel}
                    </Badge>
                    {isEntryUploaded(entry) && (
                      <Badge variant="outline" className="h-5 px-1.5 text-[11px]">
                        {t('cameras.alreadyUploaded')}
                      </Badge>
                    )}
                  </div>
                  <p className="text-xs text-muted-foreground mt-0.5">
                    {entry.sourceLabel}
                    {entry.command ? ` · ${entry.command}` : ''}
                  </p>
                  <p className="text-[11px] text-muted-foreground/80 font-mono truncate mt-0.5">
                    {entry.filename}
                  </p>
                </div>
                {!isEntryUploaded(entry) && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-8 px-2 shrink-0"
                    disabled={Boolean(bulkProgress) || requesting === entry.filename}
                    onClick={() => handleUpload(entry.filename)}
                    title={t('cameras.uploadThisFile')}
                  >
                    {requesting === entry.filename
                      ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
                      : <Upload className="h-3.5 w-3.5" />}
                  </Button>
                )}
              </div>
            ))
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}

interface JimiRecordingsDialogProps {
  device: Device;
  onClose: () => void;
}

function JimiRecordingsDialog({ device, onClose }: JimiRecordingsDialogProps) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const qc = useQueryClient();
  const imei = device.uniqueId;
  const [fileListRecording, setFileListRecording] = useState<DeviceRecording | null>(null);
  const [selectedDate, setSelectedDate] = useState<Date | undefined>();

  const { data, isLoading } = useQuery({
    queryKey: ['recordings', imei, 1000],
    queryFn: () => fetchRecordings({ imei, limit: 1000 }),
  });

  const deleteMutation = useMutation({
    mutationFn: deleteRecording,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['recordings', imei] });
      toast({ title: t('cameras.recordingDeleted') });
    },
  });

  const recordings = data?.data ?? [];
  const totalRecordings = data?.total ?? 0;
  const selectedDateKey = selectedDate ? format(selectedDate, 'yyyy-MM-dd') : null;
  const filteredRecordings = selectedDateKey
    ? recordings.filter(rec => getJimiRecordingDateKey(rec) === selectedDateKey)
    : recordings;

  return (
    <Dialog open onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent className="max-w-xl w-full gap-0" aria-describedby={undefined}>
        <DialogTitle className="flex items-center gap-2 pb-4 border-b">
          <Film className="h-4 w-4 text-muted-foreground" />
          {t('cameras.recordingsFor', { name: device.name })}
        </DialogTitle>

        <div className="py-3 border-b flex items-center justify-between gap-3">
          <span className="text-xs text-muted-foreground">
            {filteredRecordings.length} / {totalRecordings || recordings.length} {t('cameras.clipsShown')}
          </span>
          <div className="flex items-center gap-1.5">
            {selectedDate && (
              <Button variant="ghost" size="sm" className="h-8 px-2 text-xs" onClick={() => setSelectedDate(undefined)}>
                {t('cameras.allDates')}
              </Button>
            )}
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" size="sm" className="h-8 px-2 text-xs gap-1.5">
                  <CalendarIcon className="h-3.5 w-3.5" />
                  {selectedDate ? format(selectedDate, 'MMM d, yyyy') : t('cameras.filterDate')}
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="end">
                <CalendarComponent
                  mode="single"
                  selected={selectedDate}
                  onSelect={setSelectedDate}
                  disabled={(date) => date > new Date()}
                  initialFocus
                />
              </PopoverContent>
            </Popover>
          </div>
        </div>

        <div className="pt-3 max-h-[430px] overflow-y-auto pr-1">
          {isLoading ? (
            <div className="flex justify-center py-12">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : filteredRecordings.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-10">
              {selectedDate ? t('cameras.noRecordingsForDate') : t('cameras.noJimiRecordings')}
            </p>
          ) : (
            <>
              {filteredRecordings.map(rec => (
                <JimiRecordingRow
                  key={rec.id}
                  rec={rec}
                  onDelete={(id) => deleteMutation.mutate(id)}
                  deleting={deleteMutation.isPending}
                  onOpenFileList={setFileListRecording}
                />
              ))}
            </>
          )}
        </div>
        {fileListRecording && (
          <SdCardFileListDialog
            device={device}
            rec={fileListRecording}
            recordings={recordings}
            onClose={() => setFileListRecording(null)}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

// --- Jimi IoT Hub ---

function JimiWebRtcFeed({ whepUrl }: { whepUrl: string }) {
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    const abort = new AbortController();
    let pc: RTCPeerConnection;
    let retryTimer: ReturnType<typeof setTimeout>;

    async function connect() {
      pc = new RTCPeerConnection({ iceServers: [{ urls: 'stun:stun.l.google.com:19302' }] });
      pc.ontrack = ({ streams }) => {
        if (videoRef.current && streams[0]) videoRef.current.srcObject = streams[0];
      };
      pc.addTransceiver('video', { direction: 'recvonly' });
      pc.addTransceiver('audio', { direction: 'recvonly' });
      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);
      await new Promise<void>((resolve) => {
        if (pc.iceGatheringState === 'complete') { resolve(); return; }
        const timer = setTimeout(resolve, 5000);
        pc.onicegatheringstatechange = () => {
          if (pc.iceGatheringState === 'complete') { clearTimeout(timer); resolve(); }
        };
      });
      const res = await fetch(whepUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/sdp' },
        body: pc.localDescription!.sdp,
        signal: abort.signal,
      });
      if (!res.ok) throw new Error(`WHEP ${res.status}`);
      await pc.setRemoteDescription({ type: 'answer', sdp: await res.text() });
    }

    function connectWithRetry(attempt = 0) {
      connect().catch(() => {
        if (abort.signal.aborted) return;
        if (attempt < 8) retryTimer = setTimeout(() => connectWithRetry(attempt + 1), 2000);
      });
    }

    connectWithRetry();

    return () => {
      abort.abort();
      clearTimeout(retryTimer);
      pc?.close();
    };
  }, [whepUrl]);

  return (
    <video ref={videoRef} autoPlay controls playsInline className="w-full h-full object-contain" />
  );
}

type JimiStreamState = 'idle' | 'starting' | 'playing' | 'error';

interface JimiCameraCardProps {
  device: Device;
  channel: number;
  onExpand: () => void;
  onRecordings: () => void;
}

function JimiCameraCard({ device, channel, onExpand, onRecordings }: JimiCameraCardProps) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [streamState, setStreamState] = useState<JimiStreamState>('idle');
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [requestingFileList, setRequestingFileList] = useState(false);
  const isOnline = device.status === 'online';
  const imei = device.uniqueId;
  const whepUrl = getJimiWhepUrl(imei, channel);
  const label = `${device.name} — CH${channel + 1}`;

  const handleStart = async () => {
    setStreamState('starting');
    setErrorMsg(null);
    try {
      await startJimiStreaming(device.id, channel);
    } catch (e) {
      setStreamState('error');
      setErrorMsg(e instanceof Error ? e.message : String(e));
      return;
    }
    await new Promise(r => setTimeout(r, 3000));
    setStreamState('playing');
  };

  const handleRequestFileList = async () => {
    setRequestingFileList(true);
    try {
      await requestJimiSdCardFileList(device.id);
      toast({ title: t('cameras.fileListRequested') });
    } catch (e) {
      toast({ title: t('cameras.fileListRequestError'), variant: 'destructive' });
    } finally {
      setRequestingFileList(false);
    }
  };

  return (
    <div className="rounded-lg border bg-card overflow-hidden">
      <div className="relative bg-black aspect-video group">
        {streamState === 'idle' && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-3">
            <Camera className="h-10 w-10 opacity-30 text-white" />
            <Button onClick={handleStart} disabled={!isOnline} variant="secondary" size="sm">
              <Play className="h-3.5 w-3.5 mr-1.5" />
              {t('cameras.startLive')}
            </Button>
          </div>
        )}
        {streamState === 'starting' && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 text-white/70">
            <Loader2 className="h-8 w-8 animate-spin" />
            <p className="text-sm">{t('cameras.connecting')}</p>
          </div>
        )}
        {streamState === 'error' && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 p-4 text-center">
            <AlertCircle className="h-8 w-8 text-destructive" />
            <p className="text-xs text-white/70 max-w-[240px] break-words">{errorMsg}</p>
            <Button onClick={handleStart} variant="secondary" size="sm">{t('cameras.retryStream')}</Button>
          </div>
        )}
        {streamState === 'playing' && <JimiWebRtcFeed whepUrl={whepUrl} />}
        <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
          <Button
            variant="secondary"
            size="icon"
            className="h-7 w-7 bg-black/60 hover:bg-black/80 text-white border-0"
            onClick={() => navigate(`/?deviceId=${device.id}`)}
            title={t('cameras.viewOnMap')}
          >
            <ExternalLink className="h-3.5 w-3.5" />
          </Button>
          {streamState === 'playing' && (
            <Button
              variant="secondary"
              size="icon"
              className="h-7 w-7 bg-black/60 hover:bg-black/80 text-white border-0"
              onClick={onExpand}
              title={t('cameras.expand')}
            >
              <Maximize2 className="h-3.5 w-3.5" />
            </Button>
          )}
        </div>
      </div>
      <div className="flex items-center justify-between px-3 py-2.5">
        <span className="font-medium text-sm truncate">{label}</span>
        <div className="flex items-center gap-1.5">
          <Button
            variant="ghost"
            size="sm"
            className="h-7 px-2 text-xs text-muted-foreground hover:text-foreground"
            onClick={onRecordings}
            title={t('cameras.recordings')}
          >
            <Film className="h-3.5 w-3.5 mr-1" />
            {t('cameras.recordings')}
          </Button>
          <Button
            variant="ghost"
            size="sm"
            className="h-7 px-2 text-xs text-muted-foreground hover:text-foreground"
            disabled={requestingFileList || !isOnline}
            onClick={handleRequestFileList}
            title={t('cameras.requestFileList')}
          >
            {requestingFileList
              ? <Loader2 className="h-3.5 w-3.5 animate-spin" />
              : <ListVideo className="h-3.5 w-3.5" />}
          </Button>
          {isOnline
            ? <Wifi className="h-3.5 w-3.5 text-success" />
            : <WifiOff className="h-3.5 w-3.5 text-muted-foreground" />}
          <span className={`text-xs ${isOnline ? 'text-success' : 'text-muted-foreground'}`}>
            {isOnline ? t('devices.online') : t('devices.offline')}
          </span>
        </div>
      </div>
    </div>
  );
}

// --- Camera URL helpers ---

const CAMERA_URL_KEYS = ['cameraUrl', 'streamUrl', 'camera', 'videoUrl'] as const;
const CAMERA_URL2_KEYS = ['cameraUrl2', 'streamUrl2', 'videoUrl2'] as const;

const getCameraUrls = (device: Device): string[] => {
  const attrs = device.attributes || {};
  const urls: string[] = [];
  for (const key of CAMERA_URL_KEYS) {
    const val = attrs[key];
    if (typeof val === 'string' && val.trim()) { urls.push(val.trim()); break; }
  }
  for (const key of CAMERA_URL2_KEYS) {
    const val = attrs[key];
    if (typeof val === 'string' && val.trim()) { urls.push(val.trim()); break; }
  }
  return urls;
};

// Inject cameraUser/cameraPassword attributes into rtsp:// URL
// Returns { displayUrl, authUrl } — authUrl has credentials embedded, displayUrl does not
const buildCameraUrls = (device: Device, rawUrl: string): { displayUrl: string; authUrl: string } => {
  const attrs = device.attributes || {};
  const user = typeof attrs.cameraUser === 'string' ? attrs.cameraUser.trim() : '';
  const pass = typeof attrs.cameraPassword === 'string' ? attrs.cameraPassword.trim() : '';

  if (!user || !(rawUrl.startsWith('rtsp://') || rawUrl.startsWith('rtsps://'))) {
    return { displayUrl: rawUrl, authUrl: rawUrl };
  }

  const scheme = rawUrl.startsWith('rtsps://') ? 'rtsps' : 'rtsp';
  const rest = rawUrl.slice(scheme.length + 3); // strip "rtsp://"
  const authUrl = `${scheme}://${encodeURIComponent(user)}:${encodeURIComponent(pass)}@${rest}`;
  return { displayUrl: rawUrl, authUrl };
};

type StreamType = 'hls' | 'iframe' | 'rtsp' | 'insecure';

const getStreamType = (url: string): StreamType => {
  if (url.startsWith('rtsp://') || url.startsWith('rtsps://')) return 'rtsp';
  if (!url.startsWith('https://')) return 'insecure';
  if (url.includes('.m3u8')) return 'hls';
  return 'iframe';
};

function RtspPlaceholder({ displayUrl, authUrl }: { displayUrl: string; authUrl: string }) {
  const { t } = useTranslation();
  const [copied, setCopied] = useState(false);

  const copy = () => {
    navigator.clipboard.writeText(authUrl).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <div className="absolute inset-0 flex flex-col items-center justify-center text-white/70 text-sm gap-3 p-4 text-center">
      <Camera className="h-10 w-10 opacity-40" />
      <p className="font-medium text-white/90">{t('cameras.rtspStream')}</p>
      <p className="text-xs text-white/50 max-w-[280px] break-all">{displayUrl}</p>
      <div className="flex gap-2 mt-1">
        <Button
          variant="ghost"
          size="sm"
          className="text-white/70 hover:text-white border border-white/20"
          onClick={copy}
        >
          {copied
            ? <><Check className="h-3.5 w-3.5 mr-1" />{t('cameras.copied')}</>
            : <><Copy className="h-3.5 w-3.5 mr-1" />{t('cameras.copyUrl')}</>}
        </Button>
        <Button variant="ghost" size="sm" className="text-white/70 hover:text-white border border-white/20" asChild>
          <a href={authUrl}>
            <ExternalLink className="h-3.5 w-3.5 mr-1" />
            {t('cameras.openInPlayer')}
          </a>
        </Button>
      </div>
    </div>
  );
}

interface CameraFeedProps {
  displayUrl: string;
  authUrl: string;
  title: string;
}

function CameraFeed({ displayUrl, authUrl, title }: CameraFeedProps) {
  const { t } = useTranslation();
  const type = getStreamType(displayUrl);

  if (type === 'rtsp') {
    return <RtspPlaceholder displayUrl={displayUrl} authUrl={authUrl} />;
  }

  if (type === 'insecure') {
    return (
      <div className="absolute inset-0 flex flex-col items-center justify-center text-white/70 text-sm gap-2 p-4 text-center">
        <Camera className="h-8 w-8 opacity-50" />
        <p>{t('cameras.insecureStream')}</p>
        <Button variant="ghost" size="sm" className="text-white/70 hover:text-white" asChild>
          <a href={displayUrl} target="_blank" rel="noopener noreferrer">
            <ExternalLink className="h-3.5 w-3.5 mr-1" />
            {t('cameras.openStream')}
          </a>
        </Button>
      </div>
    );
  }

  if (type === 'hls') {
    return (
      <video
        src={displayUrl}
        autoPlay
        muted
        controls
        playsInline
        className="w-full h-full object-contain"
        title={title}
      />
    );
  }

  return (
    <iframe
      src={displayUrl}
      className="w-full h-full border-0"
      allowFullScreen
      title={title}
    />
  );
}

interface CameraCardProps {
  device: Device;
  streamUrl: string;
  channel?: number;
  onExpand: () => void;
  onRecordings: () => void;
}

function CameraCard({ device, streamUrl, channel, onExpand, onRecordings }: CameraCardProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const isOnline = device.status === 'online';
  const { displayUrl, authUrl } = buildCameraUrls(device, streamUrl);
  const label = channel != null ? `${device.name} — CH${channel}` : device.name;
  const hasRecordings = true

  return (
    <div className="rounded-lg border bg-card overflow-hidden">
      <div className="relative bg-black aspect-video group">
        <CameraFeed displayUrl={displayUrl} authUrl={authUrl} title={label} />
        <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
          <Button
            variant="secondary"
            size="icon"
            className="h-7 w-7 bg-black/60 hover:bg-black/80 text-white border-0"
            onClick={() => navigate(`/?deviceId=${device.id}`)}
            title={t('cameras.viewOnMap')}
          >
            <ExternalLink className="h-3.5 w-3.5" />
          </Button>
          <Button
            variant="secondary"
            size="icon"
            className="h-7 w-7 bg-black/60 hover:bg-black/80 text-white border-0"
            onClick={onExpand}
            title={t('cameras.expand')}
          >
            <Maximize2 className="h-3.5 w-3.5" />
          </Button>
        </div>
      </div>

      <div className="flex items-center justify-between px-3 py-2.5">
        <span className="font-medium text-sm truncate">{label}</span>
        <div className="flex items-center gap-2 shrink-0 ml-2">
          {hasRecordings && (
            <Button
              variant="ghost"
              size="sm"
              className="h-7 px-2 text-xs text-muted-foreground hover:text-foreground"
              onClick={onRecordings}
              title={t('cameras.recordings')}
            >
              <Film className="h-3.5 w-3.5 mr-1" />
              {t('cameras.recordings')}
            </Button>
          )}
          <div className="flex items-center gap-1.5">
            {isOnline
              ? <Wifi className="h-3.5 w-3.5 text-success" />
              : <WifiOff className="h-3.5 w-3.5 text-muted-foreground" />}
            <span className={`text-xs ${isOnline ? 'text-success' : 'text-muted-foreground'}`}>
              {isOnline ? t('devices.online') : t('devices.offline')}
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}

// --- JT/T 1078 (Jimi JC181 / JC371 / JC450 via our media server) ---

interface Jt1078CameraCardProps {
  device: Device;
  channel: number;
  onExpand: () => void;
}

function Jt1078CameraCard({ device, channel, onExpand }: Jt1078CameraCardProps) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [streamState, setStreamState] = useState<JimiStreamState>('idle');
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const isOnline = device.status === 'online';
  const imei = device.uniqueId;
  const whepUrl = getJt1078WhepUrl(imei, channel);
  const label = `${device.name} — ${t('cameras.cameraN', { n: channel })}`;

  const handleStart = async () => {
    setStreamState('starting');
    setErrorMsg(null);
    try {
      await startJt1078Streaming(device.id, channel);
    } catch (e) {
      setStreamState('error');
      setErrorMsg(e instanceof Error ? e.message : String(e));
      return;
    }
    // The camera connects to the media server on its own; wait for video before playing.
    if (await waitForJt1078Video(imei, channel)) {
      setStreamState('playing');
    } else {
      setStreamState('error');
      setErrorMsg(t('cameras.noVideo'));
    }
  };

  return (
    <div className="rounded-lg border bg-card overflow-hidden">
      <div className="relative bg-black aspect-video group">
        {streamState === 'idle' && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-3">
            <Camera className="h-10 w-10 opacity-30 text-white" />
            <Button onClick={handleStart} disabled={!isOnline} variant="secondary" size="sm">
              <Play className="h-3.5 w-3.5 mr-1.5" />
              {t('cameras.startLive')}
            </Button>
          </div>
        )}
        {streamState === 'starting' && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 text-white/70">
            <Loader2 className="h-8 w-8 animate-spin" />
            <p className="text-sm">{t('cameras.connecting')}</p>
          </div>
        )}
        {streamState === 'error' && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 p-4 text-center">
            <AlertCircle className="h-8 w-8 text-destructive" />
            <p className="text-xs text-white/70 max-w-[240px] break-words">{errorMsg}</p>
            <Button onClick={handleStart} variant="secondary" size="sm">{t('cameras.retryStream')}</Button>
          </div>
        )}
        {streamState === 'playing' && <JimiWebRtcFeed whepUrl={whepUrl} />}
        <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
          <Button
            variant="secondary"
            size="icon"
            className="h-7 w-7 bg-black/60 hover:bg-black/80 text-white border-0"
            onClick={() => navigate(`/?deviceId=${device.id}`)}
            title={t('cameras.viewOnMap')}
          >
            <ExternalLink className="h-3.5 w-3.5" />
          </Button>
          {streamState === 'playing' && (
            <Button
              variant="secondary"
              size="icon"
              className="h-7 w-7 bg-black/60 hover:bg-black/80 text-white border-0"
              onClick={onExpand}
              title={t('cameras.expand')}
            >
              <Maximize2 className="h-3.5 w-3.5" />
            </Button>
          )}
        </div>
      </div>
      <div className="flex items-center justify-between px-3 py-2.5">
        <span className="font-medium text-sm truncate">{label}</span>
        <div className="flex items-center gap-1.5">
          {isOnline
            ? <Wifi className="h-3.5 w-3.5 text-success" />
            : <WifiOff className="h-3.5 w-3.5 text-muted-foreground" />}
          <span className={`text-xs ${isOnline ? 'text-success' : 'text-muted-foreground'}`}>
            {isOnline ? t('devices.online') : t('devices.offline')}
          </span>
        </div>
      </div>
    </div>
  );
}

type CameraEntry =
  | { type: 'stream'; device: Device; url: string; channel?: number }
  | { type: 'jimi'; device: Device; channel: number }
  | { type: 'jt1078'; device: Device; channel: number };

const Cameras = () => {
  const { t } = useTranslation();
  const { isAuthenticated } = useTraccarSession();
  const [searchQuery, setSearchQuery] = useState('');
  const [expanded, setExpanded] = useState<
    | { type: 'stream'; device: Device; url: string }
    | { type: 'webrtc'; device: Device; whepUrl: string }
    | null
  >(null);
  const [recordingsDevice, setRecordingsDevice] = useState<Device | null>(null);
  const [jimiRecordingsDevice, setJimiRecordingsDevice] = useState<Device | null>(null);

  const { data: devices = [], isLoading } = useQuery({
    queryKey: ['devices'],
    queryFn: fetchDevices,
    enabled: isAuthenticated,
    staleTime: 30000,
    gcTime: 5 * 60 * 1000,
  });

  const cameras = useMemo((): CameraEntry[] =>
    devices.flatMap(d => {
      if (isJt1078Camera(d)) {
        return getJt1078Channels(d).map(channel => ({ type: 'jt1078' as const, device: d, channel }));
      }
      if (d.model === 'JC400P') {
        return Array.from({ length: 2 }, (_, i) => ({
          type: 'jimi' as const,
          device: d,
          channel: i,
        }));
      }
      const urls = getCameraUrls(d);
      if (urls.length === 0) return [];
      if (urls.length === 1) return [{ type: 'stream' as const, device: d, url: urls[0], channel: undefined }];
      return urls.map((url, i) => ({ type: 'stream' as const, device: d, url, channel: i + 1 }));
    }),
    [devices]
  );

  const filteredCameras = useMemo(() => {
    const q = searchQuery.toLowerCase();
    const matching = searchQuery ? cameras.filter(c => c.device.name.toLowerCase().includes(q)) : cameras;
    // Online cameras first, then by camera number (every vehicle's main/front camera first), then name.
    return [...matching].sort((a, b) =>
      Number(b.device.status === 'online') - Number(a.device.status === 'online') ||
      (a.channel ?? 0) - (b.channel ?? 0) ||
      a.device.name.localeCompare(b.device.name));
  }, [cameras, searchQuery]);

  const onlineCount = useMemo(() => {
    const seen = new Set<number>();
    return cameras.filter(c => {
      if (seen.has(c.device.id)) return false;
      seen.add(c.device.id);
      return c.device.status === 'online';
    }).length;
  }, [cameras]);

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="h-12 w-12 animate-spin text-primary" />
          <p className="text-muted-foreground">{t('common.loading')}</p>
        </div>
      </div>
    );
  }

  const headerContent = cameras.length > 0 ? (
    <div className="relative">
      <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
      <Input
        placeholder={t('cameras.searchPlaceholder')}
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        className="pl-9 w-64"
      />
    </div>
  ) : undefined;

  return (
    <DashboardLayout headerContent={headerContent}>
      <div className="p-6 space-y-6">
        {cameras.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-24 text-muted-foreground">
            <Camera className="h-16 w-16 mb-4 opacity-30" />
            <p className="text-lg font-medium">{t('cameras.noCameras')}</p>
            <p className="text-sm mt-1 max-w-sm text-center">{t('cameras.noCamerasHint')}</p>
          </div>
        ) : (
          <>
            <div className="flex items-center gap-6 text-sm">
              <div className="flex items-center gap-2">
                <Camera className="h-4 w-4 text-muted-foreground" />
                <span className="text-muted-foreground">{t('cameras.total')}:</span>
                <span className="font-medium">{cameras.length}</span>
              </div>
              <div className="flex items-center gap-2">
                <Wifi className="h-4 w-4 text-success" />
                <span className="text-muted-foreground">{t('devices.online')}:</span>
                <span className="font-medium text-success">{onlineCount}</span>
              </div>
            </div>

            {filteredCameras.length === 0 ? (
              <div className="text-center py-16 text-muted-foreground">
                <p>{t('cameras.noMatch')}</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
                {filteredCameras.map((entry) => {
                  const key = `${entry.device.id}-${entry.type}-${entry.channel ?? 0}`;
                  if (entry.type === 'jt1078') {
                    return (
                      <Jt1078CameraCard
                        key={key}
                        device={entry.device}
                        channel={entry.channel}
                        onExpand={() => setExpanded({ type: 'webrtc', device: entry.device, whepUrl: getJt1078WhepUrl(entry.device.uniqueId, entry.channel) })}
                      />
                    );
                  }
                  if (entry.type === 'jimi') {
                    return (
                      <JimiCameraCard
                        key={key}
                        device={entry.device}
                        channel={entry.channel}
                        onExpand={() => setExpanded({ type: 'webrtc', device: entry.device, whepUrl: getJimiWhepUrl(entry.device.uniqueId, entry.channel) })}
                        onRecordings={() => setJimiRecordingsDevice(entry.device)}
                      />
                    );
                  }
                  return (
                    <CameraCard
                      key={key}
                      device={entry.device}
                      streamUrl={entry.url}
                      channel={entry.channel}
                      onExpand={() => setExpanded({ type: 'stream', device: entry.device, url: entry.url })}
                      onRecordings={() => setRecordingsDevice(entry.device)}
                    />
                  );
                })}
              </div>
            )}
          </>
        )}
      </div>

      {recordingsDevice && (
        <RecordingsDialog device={recordingsDevice} onClose={() => setRecordingsDevice(null)} />
      )}

      {jimiRecordingsDevice && (
        <JimiRecordingsDialog device={jimiRecordingsDevice} onClose={() => setJimiRecordingsDevice(null)} />
      )}

      {expanded && (
        <Dialog open onOpenChange={(open) => { if (!open) setExpanded(null); }}>
          <DialogContent className="max-w-5xl w-full p-0 overflow-hidden gap-0" aria-describedby={undefined}>
            <DialogTitle className="sr-only">{expanded.device.name}</DialogTitle>
            <div className="relative bg-black aspect-video">
              {expanded.type === 'webrtc'
                ? <JimiWebRtcFeed whepUrl={expanded.whepUrl} />
                : (() => {
                    const { displayUrl, authUrl } = buildCameraUrls(expanded.device, expanded.url);
                    return <CameraFeed displayUrl={displayUrl} authUrl={authUrl} title={expanded.device.name} />;
                  })()}
            </div>
            <div className="flex items-center justify-between px-4 py-3">
              <div className="min-w-0">
                <p className="font-semibold">{expanded.device.name}</p>
                <p className="text-xs text-muted-foreground truncate">
                  {expanded.type === 'webrtc' ? expanded.whepUrl : expanded.url}
                </p>
              </div>
              <Badge
                variant={expanded.device.status === 'online' ? 'default' : 'secondary'}
                className="ml-4 shrink-0"
              >
                {expanded.device.status}
              </Badge>
            </div>
          </DialogContent>
        </Dialog>
      )}
    </DashboardLayout>
  );
};

export default Cameras;
